import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import axios from 'axios';
import { 
  Calendar as CalendarIcon, Plus, Edit, Trash2, 
  BookOpen, ChevronLeft, ChevronRight, Loader2,
  CheckCircle, Clock, AlertCircle
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];

const STATUS_COLORS = {
  planned: 'bg-blue-100 text-blue-700',
  prepared: 'bg-yellow-100 text-yellow-700',
  delivered: 'bg-green-100 text-green-700'
};

const STATUS_ICONS = {
  planned: Clock,
  prepared: AlertCircle,
  delivered: CheckCircle
};

export default function PreachingCalendar({ user }) {
  const [calendar, setCalendar] = useState([]);
  const [savedSuggestions, setSavedSuggestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedEntry, setSelectedEntry] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    passage: '',
    theme: '',
    notes: '',
    status: 'planned'
  });

  const getAuthHeaders = () => {
    const token = localStorage.getItem('session_token');
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  const fetchCalendar = async () => {
    try {
      const response = await axios.get(`${API}/nextstep/calendar`, {
        headers: getAuthHeaders(),
        withCredentials: true
      });
      setCalendar(response.data.calendar || []);
    } catch (error) {
      console.error('Error fetching calendar:', error);
      toast.error('Failed to load calendar');
    }
  };

  const fetchSavedSuggestions = async () => {
    try {
      const response = await axios.get(`${API}/nextstep/saved`, {
        headers: getAuthHeaders(),
        withCredentials: true
      });
      setSavedSuggestions(response.data.saved_suggestions || []);
    } catch (error) {
      console.error('Error fetching saved suggestions:', error);
    }
  };

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchCalendar(), fetchSavedSuggestions()]);
      setLoading(false);
    };
    loadData();
  }, []);

  const getDaysInMonth = (year, month) => {
    return new Date(year, month + 1, 0).getDate();
  };

  const getFirstDayOfMonth = (year, month) => {
    return new Date(year, month, 1).getDay();
  };

  const formatDate = (year, month, day) => {
    return `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
  };

  const getEntriesForDate = (dateStr) => {
    return calendar.filter(entry => entry.date === dateStr);
  };

  const handlePrevMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1));
  };

  const handleNextMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1));
  };

  const handleDateClick = (dateStr) => {
    setSelectedDate(dateStr);
    setFormData({
      title: '',
      passage: '',
      theme: '',
      notes: '',
      status: 'planned'
    });
    setIsAddModalOpen(true);
  };

  const handleAddEntry = async () => {
    if (!formData.title.trim()) {
      toast.error('Please enter a title');
      return;
    }

    try {
      const response = await axios.post(`${API}/nextstep/calendar`, null, {
        params: {
          date: selectedDate,
          title: formData.title,
          passage: formData.passage || null,
          theme: formData.theme || null,
          notes: formData.notes || null
        },
        headers: getAuthHeaders(),
        withCredentials: true
      });

      setCalendar([...calendar, response.data.entry]);
      setIsAddModalOpen(false);
      toast.success('Calendar entry added!');
    } catch (error) {
      toast.error('Failed to add entry');
    }
  };

  const handleEditClick = (entry) => {
    setSelectedEntry(entry);
    setFormData({
      title: entry.title || '',
      passage: entry.passage || '',
      theme: entry.theme || '',
      notes: entry.notes || '',
      status: entry.status || 'planned'
    });
    setIsEditModalOpen(true);
  };

  const handleUpdateEntry = async () => {
    if (!formData.title.trim()) {
      toast.error('Please enter a title');
      return;
    }

    try {
      const response = await axios.put(`${API}/nextstep/calendar/${selectedEntry.id}`, null, {
        params: {
          title: formData.title,
          passage: formData.passage || null,
          theme: formData.theme || null,
          notes: formData.notes || null,
          status: formData.status
        },
        headers: getAuthHeaders(),
        withCredentials: true
      });

      setCalendar(calendar.map(e => 
        e.id === selectedEntry.id ? response.data.entry : e
      ));
      setIsEditModalOpen(false);
      toast.success('Entry updated!');
    } catch (error) {
      toast.error('Failed to update entry');
    }
  };

  const handleDeleteEntry = async (entryId) => {
    if (!confirm('Are you sure you want to delete this entry?')) return;

    try {
      await axios.delete(`${API}/nextstep/calendar/${entryId}`, {
        headers: getAuthHeaders(),
        withCredentials: true
      });

      setCalendar(calendar.filter(e => e.id !== entryId));
      toast.success('Entry deleted');
    } catch (error) {
      toast.error('Failed to delete entry');
    }
  };

  const handleAddSavedToCalendar = async (suggestion, dateStr) => {
    try {
      const response = await axios.post(
        `${API}/nextstep/calendar/from-saved/${suggestion.id}`,
        null,
        {
          params: { date: dateStr },
          headers: getAuthHeaders(),
          withCredentials: true
        }
      );

      setCalendar([...calendar, response.data.entry]);
      fetchSavedSuggestions(); // Refresh to update status
      toast.success('Added to calendar!');
    } catch (error) {
      toast.error('Failed to add to calendar');
    }
  };

  const year = currentDate.getFullYear();
  const month = currentDate.getMonth();
  const daysInMonth = getDaysInMonth(year, month);
  const firstDay = getFirstDayOfMonth(year, month);

  const calendarDays = [];
  for (let i = 0; i < firstDay; i++) {
    calendarDays.push(null);
  }
  for (let day = 1; day <= daysInMonth; day++) {
    calendarDays.push(day);
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-glory-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Calendar Header */}
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-kingdom-blue flex items-center gap-2">
          <CalendarIcon className="w-6 h-6 text-glory-500" />
          Preaching Calendar
        </h2>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="icon" onClick={handlePrevMonth}>
            <ChevronLeft className="w-4 h-4" />
          </Button>
          <span className="font-semibold text-lg min-w-[160px] text-center">
            {MONTHS[month]} {year}
          </span>
          <Button variant="outline" size="icon" onClick={handleNextMonth}>
            <ChevronRight className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Calendar Grid */}
      <Card>
        <CardContent className="p-4">
          {/* Day Headers */}
          <div className="grid grid-cols-7 gap-1 mb-2">
            {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
              <div key={day} className="text-center text-sm font-semibold text-gray-500 py-2">
                {day}
              </div>
            ))}
          </div>

          {/* Calendar Days */}
          <div className="grid grid-cols-7 gap-1">
            {calendarDays.map((day, index) => {
              if (day === null) {
                return <div key={index} className="h-24 bg-gray-50 rounded-lg" />;
              }

              const dateStr = formatDate(year, month, day);
              const entries = getEntriesForDate(dateStr);
              const isToday = dateStr === new Date().toISOString().split('T')[0];
              const isSunday = (firstDay + day - 1) % 7 === 0;

              return (
                <div
                  key={index}
                  className={`h-24 p-1 rounded-lg border cursor-pointer transition-colors hover:bg-glory-50 ${
                    isToday ? 'border-glory-500 bg-glory-50/50' : 'border-gray-200'
                  } ${isSunday ? 'bg-blue-50/30' : ''}`}
                  onClick={() => handleDateClick(dateStr)}
                >
                  <div className="flex items-center justify-between mb-1">
                    <span className={`text-sm font-medium ${isToday ? 'text-glory-600' : 'text-gray-700'}`}>
                      {day}
                    </span>
                    {isSunday && (
                      <Badge variant="outline" className="text-[10px] px-1 py-0">
                        Sun
                      </Badge>
                    )}
                  </div>
                  <div className="space-y-1 overflow-hidden">
                    {entries.slice(0, 2).map(entry => {
                      const StatusIcon = STATUS_ICONS[entry.status] || Clock;
                      return (
                        <div
                          key={entry.id}
                          className={`text-[10px] px-1 py-0.5 rounded truncate flex items-center gap-1 ${STATUS_COLORS[entry.status]}`}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleEditClick(entry);
                          }}
                        >
                          <StatusIcon className="w-3 h-3 flex-shrink-0" />
                          <span className="truncate">{entry.title}</span>
                        </div>
                      );
                    })}
                    {entries.length > 2 && (
                      <span className="text-[10px] text-gray-500">
                        +{entries.length - 2} more
                      </span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Saved Suggestions */}
      {savedSuggestions.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <BookOpen className="w-5 h-5 text-glory-500" />
              Saved NextStep Suggestions
            </CardTitle>
            <CardDescription>
              Drag these to your calendar or click to schedule
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {savedSuggestions.filter(s => s.status !== 'scheduled').map(saved => (
                <Card key={saved.id} className="border-glory-200">
                  <CardContent className="p-3">
                    <h4 className="font-semibold text-sm text-kingdom-blue truncate">
                      {saved.suggestion?.title}
                    </h4>
                    {saved.suggestion?.passage && (
                      <p className="text-xs text-gray-500 mt-1">
                        📖 {saved.suggestion.passage}
                      </p>
                    )}
                    <Button
                      variant="outline"
                      size="sm"
                      className="w-full mt-2"
                      onClick={() => {
                        const dateStr = prompt('Enter date (YYYY-MM-DD):');
                        if (dateStr && /^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
                          handleAddSavedToCalendar(saved, dateStr);
                        } else if (dateStr) {
                          toast.error('Please enter date in YYYY-MM-DD format');
                        }
                      }}
                    >
                      <Plus className="w-3 h-3 mr-1" />
                      Schedule
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Add Entry Modal */}
      <Dialog open={isAddModalOpen} onOpenChange={setIsAddModalOpen}>
        <DialogContent className="bg-white">
          <DialogHeader>
            <DialogTitle>Add to Calendar</DialogTitle>
            <DialogDescription>
              Schedule a sermon for {selectedDate}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label htmlFor="add-title">Title *</Label>
              <Input
                id="add-title"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                placeholder="Sermon title"
                className="mt-1"
              />
            </div>
            <div>
              <Label htmlFor="add-passage">Scripture Passage</Label>
              <Input
                id="add-passage"
                value={formData.passage}
                onChange={(e) => setFormData({ ...formData, passage: e.target.value })}
                placeholder="e.g., John 3:16"
                className="mt-1"
              />
            </div>
            <div>
              <Label htmlFor="add-theme">Theme</Label>
              <Input
                id="add-theme"
                value={formData.theme}
                onChange={(e) => setFormData({ ...formData, theme: e.target.value })}
                placeholder="e.g., Grace, Faith"
                className="mt-1"
              />
            </div>
            <div>
              <Label htmlFor="add-notes">Notes</Label>
              <Textarea
                id="add-notes"
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                placeholder="Any additional notes..."
                className="mt-1"
                rows={2}
              />
            </div>
          </div>
          <div className="flex gap-3">
            <Button variant="outline" onClick={() => setIsAddModalOpen(false)} className="flex-1">
              Cancel
            </Button>
            <Button onClick={handleAddEntry} className="flex-1 bg-kingdom-blue hover:bg-kingdom-blue/90">
              Add to Calendar
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Entry Modal */}
      <Dialog open={isEditModalOpen} onOpenChange={setIsEditModalOpen}>
        <DialogContent className="bg-white">
          <DialogHeader>
            <DialogTitle>Edit Calendar Entry</DialogTitle>
            <DialogDescription>
              Update or delete this entry
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label htmlFor="edit-title">Title *</Label>
              <Input
                id="edit-title"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                className="mt-1"
              />
            </div>
            <div>
              <Label htmlFor="edit-passage">Scripture Passage</Label>
              <Input
                id="edit-passage"
                value={formData.passage}
                onChange={(e) => setFormData({ ...formData, passage: e.target.value })}
                className="mt-1"
              />
            </div>
            <div>
              <Label htmlFor="edit-theme">Theme</Label>
              <Input
                id="edit-theme"
                value={formData.theme}
                onChange={(e) => setFormData({ ...formData, theme: e.target.value })}
                className="mt-1"
              />
            </div>
            <div>
              <Label htmlFor="edit-status">Status</Label>
              <Select
                value={formData.status}
                onValueChange={(value) => setFormData({ ...formData, status: value })}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="planned">Planned</SelectItem>
                  <SelectItem value="prepared">Prepared</SelectItem>
                  <SelectItem value="delivered">Delivered</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="edit-notes">Notes</Label>
              <Textarea
                id="edit-notes"
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                className="mt-1"
                rows={2}
              />
            </div>
          </div>
          <div className="flex gap-3">
            <Button 
              variant="destructive" 
              onClick={() => {
                handleDeleteEntry(selectedEntry.id);
                setIsEditModalOpen(false);
              }}
            >
              <Trash2 className="w-4 h-4 mr-1" />
              Delete
            </Button>
            <Button variant="outline" onClick={() => setIsEditModalOpen(false)} className="flex-1">
              Cancel
            </Button>
            <Button onClick={handleUpdateEntry} className="flex-1 bg-kingdom-blue hover:bg-kingdom-blue/90">
              Save Changes
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
